import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Guards an in-progress session (exam / review / smart review) so a stray
 * back-button, swipe-back, sidebar click, or page refresh doesn't dump the
 * student's session without a confirmation prompt.
 *
 * Works with React Router's BrowserRouter (no data-router required).
 *
 * Intercepts:
 *   1. Clicks on any in-page <a> link (sidebar nav, breadcrumb links, "Exit" link).
 *   2. Browser back button / mobile swipe-back (popstate).
 *   3. Tab close / hard refresh (beforeunload — native confirm dialog).
 *
 * Returns:
 *   - confirmOpen   : show the confirmation modal
 *   - confirmExit() : user pressed "Exit"  → release the navigation
 *   - cancelExit()  : user pressed "Stay"  → cancel the pending navigation
 */
export function useExitGuard(active) {
  const navigate = useNavigate();
  const [pendingHref, setPendingHref] = useState(null);
  const [popPending, setPopPending] = useState(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  /* ----- 1. Intercept in-app <a> clicks (sidebar, Link, etc.) -------------- */
  useEffect(() => {
    if (!active) return undefined;

    const handler = (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return; // only primary click
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      // Walk up to the closest <a>
      let el = e.target;
      while (el && el !== document.body && el.tagName !== "A") el = el.parentNode;
      if (!el || el.tagName !== "A") return;
      const href = el.getAttribute("href");
      if (!href) return;
      // Skip external/new-tab/anchor-only links
      if (el.target === "_blank") return;
      if (href.startsWith("http") && !href.startsWith(window.location.origin)) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (href.startsWith("#")) return;
      // Resolve to a path relative to origin
      let path = href;
      if (href.startsWith(window.location.origin)) {
        path = href.slice(window.location.origin.length);
      }
      if (path === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingHref(path);
    };

    document.addEventListener("click", handler, true); // capture phase
    return () => document.removeEventListener("click", handler, true);
  }, [active]);

  /* ----- 2. Intercept browser back / swipe-back via popstate --------------- */
  useEffect(() => {
    if (!active) return undefined;

    // Push a sentinel so the first "back" lands on our guard and we can prompt.
    const guardKey = "__pm_exit_guard__";
    window.history.pushState({ [guardKey]: true }, "");

    const onPop = () => {
      if (!activeRef.current) return;
      // The user has just popped past our sentinel. Re-push to lock them in
      // place until they confirm or cancel.
      window.history.pushState({ [guardKey]: true }, "");
      setPopPending(true);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, [active]);

  /* ----- 3. Tab close / hard refresh — native confirm ---------------------- */
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  /* ----- Dialog actions ---------------------------------------------------- */
  const confirmOpen = pendingHref !== null || popPending;

  const confirmExit = useCallback(() => {
    if (popPending) {
      setPopPending(false);
      // Already re-pushed by onPop; go back twice — once to skip our sentinel,
      // once to actually leave.
      window.history.go(-2);
      return;
    }
    if (pendingHref !== null) {
      const target = pendingHref;
      setPendingHref(null);
      // Defer navigation a tick so the dialog closes cleanly first.
      setTimeout(() => navigate(target), 0);
    }
  }, [pendingHref, popPending, navigate]);

  const cancelExit = useCallback(() => {
    setPendingHref(null);
    setPopPending(false);
  }, []);

  return { confirmOpen, confirmExit, cancelExit };
}
